import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Users, Trophy, Upload, UserPlus, Target, Settings, User, LogOut, PlusCircle as Plus, BarChart2 } from 'lucide-react';
import Modal, { ModalActions } from '../shared/Modal';
import PlayerManagementCard from './PlayerManagementCard';
import ConfirmDialog from '../ConfirmDialog';
import ImageCropper from '../shared/ImageCropper';
import { CoachTeamStats } from '../grind';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CoachDashboard = () => {
  const { user, logout, refreshUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showInvitePlayer, setShowInvitePlayer] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', club_name: '', sport: '', level: '' });
  const [inviteData, setInviteData] = useState({ athlete_email: '', message: '' });
  const [customAffirmation, setCustomAffirmation] = useState('');
  const [joinRequests, setJoinRequests] = useState([]);
  const [coachChallenges, setCoachChallenges] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  const [newChallenge, setNewChallenge] = useState({
    challenge_title: '',
    challenge_description: '',
    challenge_type: 'skill',
    points_reward: 25,
    team_id: null
  });
  const [loading, setLoading] = useState(true);
  const [showModuleSettings, setShowModuleSettings] = useState(false);
  const [enabledModules, setEnabledModules] = useState([]);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingData, setEditingData] = useState({});
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [showTeamStats, setShowTeamStats] = useState(false);
  
  // Compute full name from first_name and last_name, with fallback
  const userFullName = user?.first_name && user?.last_name 
    ? `${user.first_name} ${user.last_name}`.trim()
    : user?.full_name || user?.username || 'Coach';
  
  // Ref for profile section
  const profileSectionRef = useRef(null);
  
  // Scroll to profile section
  const scrollToProfile = () => {
    profileSectionRef.current?.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  };

  useEffect(() => {
    loadTeams();
    loadJoinRequests();
    loadCoachChallenges();
  }, []);

  const loadTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
      if (response.data.length > 0 && !selectedTeam) {
        const team = response.data[0];
        setSelectedTeam(team);
        setEnabledModules(team.enabled_modules || ["affirmations", "visualization", "nutrition", "hydration", "sleep"]);
      }
    } catch (error) {
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const loadJoinRequests = async () => {
    try {
      const response = await axios.get(`${API}/coach/join-requests`);
      setJoinRequests(response.data);
    } catch (error) {
      console.error('Failed to load join requests');
    }
  };

  const createTeam = async () => {
    try {
      const response = await axios.post(`${API}/teams`, newTeam);
      setTeams([...teams, response.data]);
      setSelectedTeam(response.data);
      setShowCreateTeam(false);
      setNewTeam({ name: '', club_name: '', sport: '', level: '' });
      toast.success('Team created successfully!');
    } catch (error) {
      toast.error('Failed to create team');
    }
  };

  const invitePlayer = async () => {
    if (!selectedTeam) return;
    try {
      await axios.post(`${API}/teams/${selectedTeam.id}/invite`, inviteData);
      setShowInvitePlayer(false);
      setInviteData({ athlete_email: '', message: '' });
      toast.success('Invitation sent successfully!');
    } catch (error) {
      toast.error('Failed to send invitation');
    }
  };

  const addCustomAffirmation = async () => {
    if (!selectedTeam || !customAffirmation.trim()) return;
    try {
      await axios.post(`${API}/teams/${selectedTeam.id}/affirmations`, null, {
        params: { affirmation_text: customAffirmation }
      });
      setCustomAffirmation('');
      toast.success('Custom affirmation added!');
    } catch (error) {
      toast.error('Failed to add affirmation');
    }
  };


  const loadCoachChallenges = async () => {
    try {
      const response = await axios.get(`${API}/coach/challenges`);
      setCoachChallenges(response.data.challenges || []);
    } catch (error) {
      console.error('Failed to load challenges');
    }
  };

  const handleTeamSelect = (team) => {
    setSelectedTeam(team);
    setEnabledModules(team.enabled_modules || ["affirmations", "visualization", "nutrition", "hydration", "sleep"]);
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    try {
      const response = await axios.delete(`${API}/teams/${teamId}`);
      toast.success(response.data.message);
      
      // Clear selected team if it was the deleted one
      if (selectedTeam?.id === teamId) {
        setSelectedTeam(null);
      }
      
      // Reload teams list
      await loadTeams();
    } catch (error) {
      console.error('Failed to delete team:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  const toggleModule = (module) => {
    if (enabledModules.includes(module)) {
      // Don't allow disabling all modules
      if (enabledModules.length === 1) {
        toast.error('At least one module must be enabled');
        return;
      }
      setEnabledModules(enabledModules.filter(m => m !== module));
    } else {
      setEnabledModules([...enabledModules, module]);
    }
  };

  const saveModuleSettings = async () => {
    if (!selectedTeam) return;
    
    try {
      await axios.put(`${API}/teams/${selectedTeam.id}/modules`, {
        enabled_modules: enabledModules
      });
      toast.success('Module settings saved! Team members will now see only the enabled modules.');
      setShowModuleSettings(false);
      
      // Update the team in state
      const updatedTeams = teams.map(t => 
        t.id === selectedTeam.id ? {...t, enabled_modules: enabledModules} : t
      );
      setTeams(updatedTeams);
      setSelectedTeam({...selectedTeam, enabled_modules: enabledModules});
    } catch (error) {
      toast.error('Failed to save module settings');
    }
  };

  const createChallenge = async () => {
    try {
      await axios.post(`${API}/coach/challenge`, newChallenge);
      toast.success('Challenge created successfully!');
      setNewChallenge({
        challenge_title: '',
        challenge_description: '',
        challenge_type: 'skill',
        points_reward: 25,
        team_id: null
      });
      loadCoachChallenges();
    } catch (error) {
      toast.error('Failed to create challenge');
    }
  };

  const deleteChallenge = async (challengeId) => {
    // Skip confirmation since confirm() is blocked in sandboxed environment
    // User can see immediate feedback via toast
    try {
      await axios.delete(`${API}/coach/challenge/${challengeId}`);
      toast.success('Challenge deleted successfully');
      loadCoachChallenges();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete challenge');
    }
  };


  const respondToJoinRequest = async (requestId, action, responseMessage = '') => {
    try {
      const response = await axios.post(`${API}/coach/join-requests/respond`, {
        request_id: requestId,
        action: action,
        response_message: responseMessage
      });

      toast.success(response.data.message);
      loadJoinRequests(); // Refresh requests
      loadTeams(); // Refresh team data to show new players
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond to request');
    }
  };

  const handleClubIconFileChange = async (e, teamId) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPG, or SVG.');
      e.target.value = ''; // Reset input
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size exceeds 2MB limit.');
      e.target.value = ''; // Reset input
      return;
    }

    // Find and set the team being uploaded for (for loading state)
    const uploadingTeam = teams.find(t => t.id === teamId);
    if (uploadingTeam) {
      setSelectedTeam(uploadingTeam);
    }

    // Auto-upload immediately
    setUploadingIcon(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/teams/${teamId}/club-icon`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Club icon uploaded successfully!');
      
      // Refresh teams to show new icon with cache-busting
      await loadTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload club icon');
    } finally {
      setUploadingIcon(false);
      e.target.value = ''; // Reset input for future uploads
    }
  };

  // Profile editing handlers
  const handleSaveProfile = async () => {
    setLoadingProfile(true);
    try {
      const updateData = { ...editingData };
      
      // Convert number_of_players to integer if provided
      if (updateData.number_of_players) {
        updateData.number_of_players = parseInt(updateData.number_of_players, 10);
      } else {
        delete updateData.number_of_players;
      }
      
      await axios.put(`${API}/auth/profile`, updateData);
      toast.success('Profile updated successfully!');
      setEditing(false);
      await refreshUser(); // Refresh to update user data
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG or JPG.');
      return;
    }

    // Validate file size (5MB for cropping - will be compressed after)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit.');
      return;
    }

    // Create preview and open cropper
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    
    // Reset the input so same file can be selected again
    e.target.value = '';
  };

  const handleCropComplete = (croppedFile) => {
    setProfileImageFile(croppedFile);
    
    // Create preview from cropped file
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImagePreview(reader.result);
    };
    reader.readAsDataURL(croppedFile);
    
    setShowCropper(false);
    setImageToCrop(null);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
  };

  const uploadProfileImage = async () => {
    if (!profileImageFile) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', profileImageFile);

      await axios.post(`${API}/user/profile-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Profile image uploaded successfully!');
      setProfileImageFile(null);
      setProfileImagePreview(null);
      
      // Refresh user data to show new image
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload profile image');
    } finally {
      setUploadingImage(false);
    }
  };

  const deleteProfileImage = async () => {
    if (!user.profile_image) return;
    
    if (!confirm('Are you sure you want to delete your profile image?')) return;

    try {
      await axios.delete(`${API}/user/profile-image`);
      toast.success('Profile image deleted successfully!');
      
      // Refresh user data to remove image
      await refreshUser();
    } catch (error) {
      toast.error('Failed to delete profile image');
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-blue-900 animate-pulse" />
          </div>
          <p className="text-white">Loading coach dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Image Cropper Modal */}
      {showCropper && imageToCrop && (
        <ImageCropper
          imageSrc={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={1}
          circularCrop={false}
        />
      )}
      
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950">
      <div className="bg-blue-900/95 backdrop-blur-sm border-b border-blue-700 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center">
              <Trophy className="w-6 h-6 text-blue-900" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Coach Dashboard</h1>
              <p className="text-sm text-yellow-400">{selectedTeam ? selectedTeam.name : 'No team selected'}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="font-medium text-white">{userFullName}</p>
              <p className="text-sm text-yellow-400">Coach</p>
            </div>
            <button 
              onClick={scrollToProfile}
              className="cursor-pointer transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-blue-900 rounded-lg"
              title="Go to Profile Settings"
            >
              <Avatar className="w-12 h-12 rounded-lg">
                {user?.profile_image ? (
                  <AvatarImage 
                    src={`${API}/user/${user.id}/profile-image?t=${Date.now()}`}
                    alt={userFullName}
                    className="rounded-lg"
                  />
                ) : null}
                <AvatarFallback className="bg-yellow-500 text-blue-900 font-bold rounded-lg">
                  {userFullName?.charAt(0) || 'C'}
                </AvatarFallback>
              </Avatar>
            </button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button" aria-label="Log out" className="text-yellow-400 hover:bg-blue-800">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Team Selection */}
        <Card className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-white">Team Management</CardTitle>
              <Button 
                onClick={() => setShowCreateTeam(true)} 
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-blue-900 font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Team
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {teams.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-yellow-400">No teams yet. Create your first team to get started!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {teams.map((team) => (
                  <Card 
                    key={team.id} 
                    className={`cursor-pointer border-2 transition-all ${
                      selectedTeam?.id === team.id 
                        ? 'border-yellow-500 bg-blue-800/80' 
                        : 'border-blue-600 bg-blue-900/50 hover:border-blue-400'
                    }`}
                    onClick={() => handleTeamSelect(team)}
                  >
                    <CardContent className="p-4">
                      {/* Hidden file input for this team */}
                      <input
                        type="file"
                        id={`club-icon-upload-${team.id}`}
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                        onChange={(e) => handleClubIconFileChange(e, team.id)}
                        className="hidden"
                      />
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {/* Club Icon - clickable */}
                          <div 
                            onClick={(e) => {
                              e.stopPropagation();
                              document.getElementById(`club-icon-upload-${team.id}`)?.click();
                            }}
                            className="relative group cursor-pointer"
                            title={team.club_icon ? "Click to change club icon" : "Click to upload club icon"}
                          >
                            {uploadingIcon && selectedTeam?.id === team.id ? (
                              // Show loading state during upload
                              <div className="w-10 h-10 rounded-lg bg-blue-700 border-2 border-yellow-500 flex items-center justify-center">
                                <div className="animate-spin w-5 h-5 border-2 border-yellow-500 border-t-transparent rounded-full"></div>
                              </div>
                            ) : team.club_icon ? (
                              <div className="relative">
                                <img 
                                  src={`${API}/teams/${team.id}/club-icon?t=${Date.now()}`}
                                  alt="Club icon"
                                  className="w-10 h-10 rounded-lg object-cover border-2 border-yellow-500"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 rounded-lg flex items-center justify-center transition-all">
                                  <span className="text-white text-xs opacity-0 group-hover:opacity-100">Edit</span>
                                </div>
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-blue-700 border-2 border-dashed border-yellow-500 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                                <Trophy className="w-5 h-5 text-yellow-500" />
                              </div>
                            )}
                          </div>
                          <div>
                            <h3 className="font-bold text-white">{team.name}</h3>
                            <p className="text-sm text-yellow-400">{team.club_name}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDialog({
                              open: true,
                              title: 'Delete Team?',
                              description: `Are you sure you want to permanently delete "${team.name}"? This action cannot be undone. All player affiliations with this team will be removed, but player accounts and their stats will remain intact.`,
                              confirmText: 'Delete Team',
                              cancelText: 'Cancel',
                              variant: 'destructive',
                              onConfirm: () => handleDeleteTeam(team.id, team.name)
                            });
                          }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          title="Delete team"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          </svg>
                        </Button>
                      </div>
                      <p className="text-xs text-sky-300 mt-2">{team.players?.length || 0} {team.players?.length === 1 ? 'player' : 'players'}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Join Requests */}
        {joinRequests.length > 0 && (
          <Card className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
            <CardHeader>
              <CardTitle className="text-white">Pending Join Requests ({joinRequests.length})</CardTitle>
              <CardDescription className="text-gray-300">Athletes requesting to join your teams</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {joinRequests.map((request) => (
                <div key={request.id} className="p-4 border border-blue-700 rounded-lg bg-blue-800/50">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-white">{request.athlete_name}</h4>
                        <p className="text-sm text-yellow-400">{request.athlete_email}</p>
                        <p className="text-sm text-sky-300">Requesting to join: {request.club_name} - {request.team_name}</p>
                        <p className="text-sm text-gray-400">Submitted: {new Date(request.created_at).toLocaleDateString()}</p>
                        {request.message && (
                          <p className="text-sm text-gray-300 mt-2 italic">"{request.message}"</p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        size="sm"
                        onClick={() => respondToJoinRequest(request.id, 'approve')}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        Approve
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => respondToJoinRequest(request.id, 'reject')}
                        variant="outline"
                        className="border-red-500 text-red-400 hover:bg-red-600 hover:text-white"
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Team Actions */}
        {selectedTeam && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Enhanced Player Management */}
            <PlayerManagementCard 
              selectedTeam={selectedTeam}
              allTeams={teams}
              onShowInvitePlayer={() => setShowInvitePlayer(true)}
              onRefreshTeams={loadTeams}
            />

            {/* Custom Affirmations */}
            <Card className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
              <CardHeader>
                <CardTitle className="text-white">Team Affirmations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Add Custom Affirmation</Label>
                  <Textarea
                    value={customAffirmation}
                    onChange={(e) => setCustomAffirmation(e.target.value)}
                    placeholder="Enter a motivational affirmation for your team..."
                    rows={3}
                    className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                  />
                  <Button 
                    onClick={addCustomAffirmation}
                    disabled={!customAffirmation.trim()}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold"
                  >
                    Add Affirmation
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Team Game Stats */}
            <Card className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart2 className="w-5 h-5 text-purple-400" />
                  Game Stats & Analytics
                </CardTitle>
                <CardDescription className="text-gray-300">
                  View player performance data from video reviews
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => setShowTeamStats(true)}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white"
                  data-testid="view-team-stats-btn"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  View Team Performance
                </Button>
              </CardContent>
            </Card>

            {/* Module Settings */}
            <Card className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
              <CardHeader>
                <CardTitle className="text-white">Module Settings</CardTitle>
                <p className="text-sm text-gray-300 mt-1">Control which training modules your team can access</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <p className="text-sm text-sky-300">
                    Enable or disable specific modules for your team. Players will only see the modules you enable.
                  </p>
                  
                  <div className="space-y-2 bg-blue-900/50 p-4 rounded-lg">
                    {[
                      { key: 'affirmations', label: 'Affirmations', icon: '🧠', desc: 'Daily mental affirmations' },
                      { key: 'visualization', label: 'Visualization', icon: '👁️', desc: 'Mental imagery practice' },
                      { key: 'nutrition', label: 'Nutrition', icon: '🍎', desc: 'Meal tracking and nutrition' },
                      { key: 'hydration', label: 'Hydration', icon: '💧', desc: 'Water intake tracking' },
                      { key: 'sleep', label: 'Sleep', icon: '💤', desc: 'Sleep quality and recovery' }
                    ].map((module) => (
                      <label 
                        key={module.key}
                        className="flex items-center space-x-3 p-3 bg-blue-900/30 rounded border border-blue-700 cursor-pointer hover:border-yellow-500 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={enabledModules.includes(module.key)}
                          onChange={() => toggleModule(module.key)}
                          className="rounded border-blue-300 w-5 h-5"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-xl">{module.icon}</span>
                            <span className="text-white font-medium">{module.label}</span>
                          </div>
                          <p className="text-xs text-gray-400 ml-7">{module.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  
                  <div className="bg-yellow-900/30 border border-yellow-600/40 rounded p-3">
                    <p className="text-xs text-yellow-300">
                      <strong>Note:</strong> {enabledModules.length} of 5 modules enabled. At least one module must remain active.
                    </p>
                  </div>
                  
                  <Button 
                    onClick={saveModuleSettings}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold"
                  >
                    Save Module Settings
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Coach Challenges */}
            <Card className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
              <CardHeader>
                <CardTitle className="text-white">Team Challenges</CardTitle>
                <p className="text-sm text-gray-300 mt-1">Create skill-based challenges for your teams</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-white">Challenge Title</Label>
                    <Input
                      value={newChallenge.challenge_title}
                      onChange={(e) => setNewChallenge({...newChallenge, challenge_title: e.target.value})}
                      placeholder="e.g., 100 Push-ups Challenge"
                      className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white">Description</Label>
                    <Textarea
                      value={newChallenge.challenge_description}
                      onChange={(e) => setNewChallenge({...newChallenge, challenge_description: e.target.value})}
                      placeholder="Describe the challenge requirements..."
                      rows={3}
                      className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white">Type</Label>
                      <select
                        value={newChallenge.challenge_type}
                        onChange={(e) => setNewChallenge({...newChallenge, challenge_type: e.target.value})}
                        className="w-full px-3 py-2 border border-blue-700 rounded-md focus:border-yellow-500 focus:outline-none bg-blue-900/50 text-white"
                      >
                        <option value="skill" className="bg-blue-900 text-white">Skill</option>
                        <option value="fitness" className="bg-blue-900 text-white">Fitness</option>
                        <option value="mental" className="bg-blue-900 text-white">Mental</option>
                        <option value="nutrition" className="bg-blue-900 text-white">Nutrition</option>
                      </select>
                    </div>
                    
                    <div>
                      <Label className="text-white">Points Reward</Label>
                      <Input
                        type="number"
                        value={newChallenge.points_reward}
                        onChange={(e) => setNewChallenge({...newChallenge, points_reward: parseInt(e.target.value) || 25})}
                        className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white">Assign To</Label>
                    <select
                      value={newChallenge.team_id || 'all'}
                      onChange={(e) => setNewChallenge({...newChallenge, team_id: e.target.value === 'all' ? null : e.target.value})}
                      className="w-full px-3 py-2 border border-blue-700 rounded-md focus:border-yellow-500 focus:outline-none bg-blue-900/50 text-white"
                    >
                      <option value="all" className="bg-blue-900 text-white">All My Teams</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id} className="bg-blue-900 text-white">
                          {team.name} - {team.club_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <Button 
                    onClick={createChallenge}
                    disabled={!newChallenge.challenge_title.trim() || !newChallenge.challenge_description.trim()}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold"
                  >
                    Create Challenge
                  </Button>
                </div>
                
                {/* Active Challenges List */}
                {coachChallenges.length > 0 && (
                  <div className="space-y-2 mt-6">
                    <h4 className="font-semibold text-white border-t border-blue-700 pt-4">Active Challenges ({coachChallenges.length})</h4>
                    {coachChallenges.map((challenge) => (
                      <div key={challenge.id} className="bg-blue-800/50 p-3 rounded border border-blue-700">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-white">{challenge.challenge_title}</p>
                            <p className="text-sm text-gray-300 mt-1">{challenge.challenge_description}</p>
                            <div className="flex gap-2 mt-2 text-xs">
                              <span className="px-2 py-1 bg-sky-900 text-sky-300 rounded">{challenge.challenge_type}</span>
                              <span className="px-2 py-1 bg-yellow-900 text-yellow-300 rounded">{challenge.points_reward} pts</span>
                              {challenge.team_id ? (
                                <span className="px-2 py-1 bg-blue-900 text-blue-300 rounded">Specific Team</span>
                              ) : (
                                <span className="px-2 py-1 bg-green-900 text-green-300 rounded">All Teams</span>
                              )}
                            </div>
                          </div>
                          <Button
                            onClick={() => setConfirmDialog({
                              open: true,
                              title: 'Delete Challenge?',
                              description: `Are you sure you want to delete "${challenge.title}"? This action cannot be undone and will remove it from all players.`,
                              confirmText: 'Delete',
                              cancelText: 'Cancel',
                              variant: 'destructive',
                              onConfirm: () => deleteChallenge(challenge.id)
                            })}
                            size="sm"
                            variant="outline"
                            className="border-red-500 text-red-400 hover:bg-red-600 hover:text-white ml-2"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>


            {/* Coach Profile Card */}
            <Card ref={profileSectionRef} className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">Coach Profile</CardTitle>
                  {!editing && (
                    <Button 
                      onClick={() => {
                        setEditingData({
                          first_name: user?.first_name || '',
                          last_name: user?.last_name || '',
                          email: user?.email || '',
                          organization_name: user?.organization_name || '',
                          coaching_sport: user?.coaching_sport || '',
                          coaching_experience: user?.coaching_experience || '',
                          team_level: user?.team_level || '',
                          number_of_players: user?.number_of_players || ''
                        });
                        setEditing(true);
                      }} 
                      variant="outline" 
                      size="sm"
                      className="border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-blue-900"
                    >
                      Edit Profile
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {!editing ? (
                  <>
                    <div className="flex items-start space-x-4">
                      {/* Profile Image Section */}
                      <div className="flex flex-col items-center space-y-2">
                        <Avatar className="w-20 h-20 rounded-lg">
                          {user?.profile_image ? (
                            <AvatarImage 
                              src={`${API}/user/${user.id}/profile-image?t=${Date.now()}`}
                              alt={userFullName}
                              className="rounded-lg"
                            />
                          ) : null}
                          <AvatarFallback className="bg-yellow-500 text-blue-900 text-2xl font-bold rounded-lg">
                            {userFullName?.charAt(0) || 'C'}
                          </AvatarFallback>
                        </Avatar>
                        
                        {/* Upload/Delete Controls */}
                        <div className="flex flex-col space-y-1">
                          <input
                            type="file"
                            id="coach-profile-image-upload"
                            accept="image/png,image/jpeg,image/jpg"
                            onChange={handleProfileImageChange}
                            className="hidden"
                          />
                          <label htmlFor="coach-profile-image-upload">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="w-full border-sky-400 text-sky-300 hover:bg-sky-900 text-xs"
                              onClick={() => document.getElementById('coach-profile-image-upload').click()}
                            >
                              {user?.profile_image ? 'Change' : 'Upload'}
                            </Button>
                          </label>
                          {user?.profile_image && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={deleteProfileImage}
                              className="w-full border-red-500 text-red-400 hover:bg-red-600 hover:text-white text-xs"
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* User Info */}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white">{userFullName}</h3>
                        <p className="text-yellow-400 capitalize">Coach</p>
                        <p className="text-sm text-emerald-500">@{user?.username}</p>
                      </div>
                    </div>
                    
                    {/* Preview Upload Section */}
                    {profileImagePreview && (
                      <div className="p-4 bg-blue-900/50 rounded-lg border border-blue-700 space-y-3">
                        <div className="flex items-center space-x-4">
                          <img 
                            src={profileImagePreview} 
                            alt="Preview" 
                            className="w-16 h-16 rounded-lg object-cover border-2 border-yellow-500"
                          />
                          <div className="flex-1">
                            <p className="text-sm text-white font-medium">New Profile Image</p>
                            <p className="text-xs text-gray-400">{profileImageFile?.name}</p>
                            <p className="text-xs text-gray-400">
                              {(profileImageFile?.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            onClick={uploadProfileImage}
                            disabled={uploadingImage}
                            className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold"
                          >
                            {uploadingImage ? 'Uploading...' : 'Save Image'}
                          </Button>
                          <Button
                            onClick={() => {
                              setProfileImageFile(null);
                              setProfileImagePreview(null);
                            }}
                            variant="outline"
                            className="border-gray-500 text-gray-300 hover:bg-gray-600"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    <Separator />
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-50">Email</Label>
                        <p className="text-white">{user?.email}</p>
                      </div>
                      <div>
                        <Label className="text-slate-50">Organization</Label>
                        <p className="text-white">{user?.organization_name || 'Not specified'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-50">Coaching Sport</Label>
                        <p className="text-white capitalize">{user?.coaching_sport || 'Not specified'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-50">Experience</Label>
                        <p className="text-white">{user?.coaching_experience || 'Not specified'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-50">Team Level</Label>
                        <p className="text-white capitalize">{user?.team_level?.replace('_', ' ') || 'Not specified'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-50">Number of Players</Label>
                        <p className="text-white">{user?.number_of_players || 'Not specified'}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white">First Name</Label>
                        <Input
                          value={editingData.first_name}
                          onChange={(e) => setEditingData({...editingData, first_name: e.target.value})}
                          className="border-blue-700 bg-blue-900/50 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white">Last Name</Label>
                        <Input
                          value={editingData.last_name}
                          onChange={(e) => setEditingData({...editingData, last_name: e.target.value})}
                          className="border-blue-700 bg-blue-900/50 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white">Email</Label>
                        <Input
                          type="email"
                          value={editingData.email}
                          onChange={(e) => setEditingData({...editingData, email: e.target.value})}
                          className="border-blue-700 bg-blue-900/50 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white">Organization Name</Label>
                        <Input
                          value={editingData.organization_name}
                          onChange={(e) => setEditingData({...editingData, organization_name: e.target.value})}
                          className="border-blue-700 bg-blue-900/50 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white">Coaching Sport</Label>
                        <Input
                          value={editingData.coaching_sport}
                          onChange={(e) => setEditingData({...editingData, coaching_sport: e.target.value})}
                          className="border-blue-700 bg-blue-900/50 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white">Years of Experience</Label>
                        <Input
                          value={editingData.coaching_experience}
                          onChange={(e) => setEditingData({...editingData, coaching_experience: e.target.value})}
                          className="border-blue-700 bg-blue-900/50 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white">Team Level</Label>
                        <select
                          value={editingData.team_level}
                          onChange={(e) => setEditingData({...editingData, team_level: e.target.value})}
                          className="w-full px-3 py-2 border border-blue-700 rounded-md bg-blue-900/50 text-white"
                        >
                          <option value="">Select Level</option>
                          <option value="youth">Youth</option>
                          <option value="high_school">High School</option>
                          <option value="college">College</option>
                          <option value="professional">Professional</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-white">Number of Players</Label>
                        <Input
                          type="number"
                          value={editingData.number_of_players}
                          onChange={(e) => setEditingData({...editingData, number_of_players: e.target.value})}
                          className="border-blue-700 bg-blue-900/50 text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="flex space-x-2 pt-4">
                      <Button 
                        onClick={handleSaveProfile} 
                        disabled={loadingProfile}
                        className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold"
                      >
                        {loadingProfile ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button 
                        onClick={() => setEditing(false)}
                        variant="outline"
                        className="flex-1 border-gray-500 text-gray-300 hover:bg-gray-600"
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>


          </div>
        )}

        {/* Modals */}
        <Modal
          isOpen={showCreateTeam}
          onClose={() => setShowCreateTeam(false)}
          title="Create New Team"
        >
          <div className="space-y-4">
            <Input
              placeholder="Team Name"
              value={newTeam.name}
              onChange={(e) => setNewTeam({...newTeam, name: e.target.value})}
              className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
            />
            <Input
              placeholder="Club Name"
              value={newTeam.club_name}
              onChange={(e) => setNewTeam({...newTeam, club_name: e.target.value})}
              className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
            />
            <select
              value={newTeam.sport}
              onChange={(e) => setNewTeam({...newTeam, sport: e.target.value})}
              className="w-full px-3 py-2 border border-blue-700 rounded-md focus:border-yellow-500 focus:outline-none bg-blue-900/50 text-white"
            >
              <option value="" className="bg-blue-900 text-white">Select Sport</option>
              <option value="soccer" className="bg-blue-900 text-white">Soccer</option>
              <option value="football" className="bg-blue-900 text-white">Football</option>
              <option value="basketball" className="bg-blue-900 text-white">Basketball</option>
              <option value="tennis" className="bg-blue-900 text-white">Tennis</option>
              <option value="baseball" className="bg-blue-900 text-white">Baseball</option>
              <option value="volleyball" className="bg-blue-900 text-white">Volleyball</option>
              <option value="track_field" className="bg-blue-900 text-white">Track & Field</option>
              <option value="swimming" className="bg-blue-900 text-white">Swimming</option>
              <option value="other" className="bg-blue-900 text-white">Other</option>
            </select>
            <select
              value={newTeam.level}
              onChange={(e) => setNewTeam({...newTeam, level: e.target.value})}
              className="w-full px-3 py-2 border border-blue-700 rounded-md focus:border-yellow-500 focus:outline-none bg-blue-900/50 text-white"
            >
              <option value="" className="bg-blue-900 text-white">Select Level</option>
              <option value="youth" className="bg-blue-900 text-white">Youth</option>
              <option value="high_school" className="bg-blue-900 text-white">High School</option>
              <option value="college" className="bg-blue-900 text-white">College</option>
            </select>
            <ModalActions
              onConfirm={createTeam}
              onCancel={() => setShowCreateTeam(false)}
              confirmText="Create"
              cancelText="Cancel"
            />
          </div>
        </Modal>

        <Modal
          isOpen={showInvitePlayer}
          onClose={() => setShowInvitePlayer(false)}
          title="Invite Player"
        >
          <div className="space-y-4">
            <Input
              type="email"
              placeholder="Player's email address"
              value={inviteData.athlete_email}
              onChange={(e) => setInviteData({...inviteData, athlete_email: e.target.value})}
              className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
            />
            <Textarea
              placeholder="Optional message"
              value={inviteData.message}
              onChange={(e) => setInviteData({...inviteData, message: e.target.value})}
              rows={3}
              className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
            />
            <ModalActions
              onConfirm={invitePlayer}
              onCancel={() => setShowInvitePlayer(false)}
              confirmText="Send Invite"
              cancelText="Cancel"
            />
          </div>
        </Modal>

        {/* Confirm Dialog */}
        <ConfirmDialog
          open={confirmDialog.open}
          onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
          title={confirmDialog.title}
          description={confirmDialog.description}
          onConfirm={() => {
            confirmDialog.onConfirm?.();
            setConfirmDialog({ open: false });
          }}
          onCancel={() => setConfirmDialog({ open: false })}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
          variant={confirmDialog.variant}
        />
      </div>
    </div>
    
    {/* Team Stats Full Screen Modal */}
    {showTeamStats && selectedTeam && (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 z-50 overflow-auto">
        <div className="max-w-4xl mx-auto p-4">
          <CoachTeamStats
            teamId={selectedTeam.id}
            teamName={`${selectedTeam.club_name} - ${selectedTeam.name}`}
            onBack={() => setShowTeamStats(false)}
          />
        </div>
      </div>
    )}
    </>
  );
};

export default CoachDashboard;
